import { apiClient } from './apiClient';

const BASE = '/admin';

// ─── Dashboard ────────────────────────────────────────────
export const getDashboardKPIs = () =>
  apiClient.get(`${BASE}/dashboard/kpis`).then(r => r.data);

export const getDashboardSparklines = (days = 30) =>
  apiClient.get(`${BASE}/dashboard/sparklines`, { params: { days } }).then(r => r.data);

// ─── Subscriptions ────────────────────────────────────────
export const getSubscriptions = (params: Record<string, unknown>) =>
  apiClient.get(`${BASE}/subscriptions`, { params }).then(r => {
    const d = r.data;
    return { ...d, data: d.items ?? d.data ?? [] };
  });

export const getSubscriptionDetail = (id: string) =>
  apiClient.get(`${BASE}/subscriptions/${id}`).then(r => r.data);

export const extendTrial = (id: string, days: number) =>
  apiClient.post(`${BASE}/subscriptions/${id}/extend-trial`, { days }).then(r => r.data);

export const grantPremium = (userId: string, data: { days: number; reason: string }) =>
  apiClient.post(`${BASE}/subscriptions/${userId}/grant-premium`, data).then(r => r.data);

export const revokeSubscription = (id: string, reason: string) =>
  apiClient.post(`${BASE}/subscriptions/${id}/revoke`, { reason }).then(r => r.data);

// ─── Transactions ─────────────────────────────────────────
export const getTransactions = (params: Record<string, unknown>) =>
  apiClient.get(`${BASE}/transactions`, { params }).then(r => {
    const d = r.data;
    return { ...d, data: d.items ?? d.data ?? [] };
  });

export const refundTransaction = (id: string, data: { amount?: number; reason: string }) =>
  apiClient.post(`${BASE}/transactions/${id}/refund`, data).then(r => r.data);

export const exportTransactionsCsv = (params: Record<string, unknown>) =>
  apiClient.get(`${BASE}/transactions/export`, { params, responseType: 'blob' }).then(r => r.data);

// ─── Revenue ──────────────────────────────────────────────
export const getRevenueTrend = (params: { granularity: string; dateFrom?: string; dateTo?: string }) =>
  apiClient.get(`${BASE}/revenue/trend`, { params }).then(r => r.data);

export const getRevenueByCurrency = (params?: { dateFrom?: string; dateTo?: string }) =>
  apiClient.get(`${BASE}/revenue/by-currency`, { params }).then(r => r.data);

export const getPlanDistribution = () =>
  apiClient.get(`${BASE}/revenue/plan-distribution`).then(r => r.data);

export const getArpuTrend = (months = 12) =>
  apiClient.get(`${BASE}/revenue/arpu`, { params: { months } }).then(r => r.data);

export const getCreatorRevenueOverview = () =>
  apiClient.get(`${BASE}/revenue/creator-overview`).then(r => r.data);

// ─── Users ────────────────────────────────────────────────
export const getUsers = (params: Record<string, unknown>) =>
  apiClient.get(`${BASE}/users`, { params }).then(r => {
    const d = r.data;
    return { ...d, data: d.items ?? d.data ?? [] };
  });

export const getUserDetail = (id: string) =>
  apiClient.get(`${BASE}/users/${id}`).then(r => r.data);

export const changeUserRole = (id: string, role: string) =>
  apiClient.post(`${BASE}/users/${id}/role`, { role }).then(r => r.data);

export const changeAccountType = (id: string, data: { accountType: string; reason?: string }) =>
  apiClient.post(`${BASE}/users/${id}/account-type`, data).then(r => r.data);

// Trigger weekly learning report regeneration + email delivery for a user (admin-only endpoint).
export const resendWeeklyReport = (userId: string) =>
  apiClient
    .post(`/learning-reports/generate/me`, {}, { params: { targetUserId: userId, deliver: 1 } })
    .then(r => r.data);

export const sendPasswordResetEmail = (userId: string) =>
  apiClient.post(`${BASE}/users/${userId}/send-password-reset`).then(r => r.data);

export const sendVerificationEmail = (userId: string) =>
  apiClient.post(`${BASE}/users/${userId}/send-verification-email`).then(r => r.data);

export const getUserEnrollments = (userId: string, params: { page?: number; limit?: number } = {}) =>
  apiClient.get(`${BASE}/users/${userId}/enrollments`, { params }).then(r => r.data);

export const getUserNotes = (userId: string, params: { page?: number; limit?: number } = {}) =>
  apiClient.get(`${BASE}/users/${userId}/notes`, { params }).then(r => r.data);

export const getUserShadowingAttempts = (userId: string, params: { page?: number; limit?: number } = {}) =>
  apiClient.get(`${BASE}/users/${userId}/shadowing-attempts`, { params }).then(r => r.data);

export const getUserDictationAttempts = (userId: string, params: { page?: number; limit?: number } = {}) =>
  apiClient.get(`${BASE}/users/${userId}/dictation-attempts`, { params }).then(r => r.data);

export const getUserComprehensionProgress = (userId: string, params: { page?: number; limit?: number } = {}) =>
  apiClient.get(`${BASE}/users/${userId}/comprehension-progress`, { params }).then(r => r.data);

export const getUserComprehensionTranslationAttempts = (userId: string, params: { page?: number; limit?: number } = {}) =>
  apiClient.get(`${BASE}/users/${userId}/comprehension-translation-attempts`, { params }).then(r => r.data);

export const getUserQuizProgress = (userId: string, params: { page?: number; limit?: number } = {}) =>
  apiClient.get(`${BASE}/users/${userId}/quiz-progress`, { params }).then(r => r.data);

export const getUserQuizAttempts = (userId: string, params: { page?: number; limit?: number } = {}) =>
  apiClient.get(`${BASE}/users/${userId}/quiz-attempts`, { params }).then(r => r.data);

export const getUserSavedVocabulary = (userId: string, params: { page?: number; limit?: number } = {}) =>
  apiClient.get(`${BASE}/users/${userId}/saved-vocabulary`, { params }).then(r => r.data);

export const getUserSightWordMastery = (userId: string, params: { page?: number; limit?: number } = {}) =>
  apiClient.get(`${BASE}/users/${userId}/sight-word-mastery`, { params }).then(r => r.data);

export const getUserChatProfiles = (userId: string, params: { page?: number; limit?: number } = {}) =>
  apiClient.get(`${BASE}/users/${userId}/chat-profiles`, { params }).then(r => r.data);

export const getUserWritingSessions = (userId: string, params: { page?: number; limit?: number } = {}) =>
  apiClient.get(`${BASE}/users/${userId}/writing-sessions`, { params }).then(r => r.data);

export type UserActivityCounts = {
  notes: number;
  shadowing: number;
  dictation: number;
  comprehension: number;
  quiz: number;
  savedVocab: number;
  sightWord: number;
  chat: number;
  writing: number;
};

export const getUserActivityCounts = (userId: string): Promise<UserActivityCounts> =>
  apiClient.get(`${BASE}/users/${userId}/activity-counts`).then(r => r.data);

export type UserHealthSignalType =
  | 'suspended'
  | 'email_unverified'
  | 'failed_payment'
  | 'trial_ending'
  | 'cancelled_soon'
  | 'no_engagement'
  | 'token_anomaly';

export type UserHealthSignal = {
  type: UserHealthSignalType;
  severity: 'info' | 'warning' | 'danger';
  data?: Record<string, number>;
};

export type UserHealth = {
  activityStatus: 'power_user' | 'active' | 'at_risk' | 'dormant' | 'churned' | 'never_active';
  activeDays7d: number;
  activeDays30d: number;
  activeDays90d: number;
  learningTime7d: number;
  learningTimePrev7d: number;
  learningTimeTrendPct: number | null;
  subscriptionState: 'trialing' | 'active' | 'cancelled' | 'expired' | 'never';
  subscriptionDaysRemaining: number | null;
  failedPayments30d: number;
  tokenBalance: number;
  tokenSpent7d: number;
  tokenSpent7dAvg: number;
  signals: UserHealthSignal[];
};

export const getUserHealth = (userId: string): Promise<UserHealth> =>
  apiClient.get(`${BASE}/users/${userId}/health`).then(r => r.data);

export const suspendUser = (id: string, reason: string) =>
  apiClient.post(`${BASE}/users/${id}/suspend`, { reason }).then(r => r.data);

export const activateUser = (id: string) =>
  apiClient.post(`${BASE}/users/${id}/activate`).then(r => r.data);

export const adjustUserTokens = (id: string, data: { amount: number; reason: string }) =>
  apiClient.post(`${BASE}/users/${id}/tokens`, data).then(r => r.data);

export type BulkUserAction = 'suspend' | 'activate' | 'changeAccountType';

export interface BulkUsersPayload {
  userIds: string[];
  action: BulkUserAction;
  reason?: string;
  accountType?: string;
}

export interface BulkUsersResult {
  total: number;
  succeeded: number;
  failed: number;
  results: Array<{ userId: string; ok: boolean; error?: string }>;
}

export const bulkUpdateUsers = (payload: BulkUsersPayload): Promise<BulkUsersResult> =>
  apiClient.post(`${BASE}/users/bulk`, payload).then(r => r.data);

// ─── Audit Log ───────────────────────────────────────────
export interface AdminAuditLogEntry {
  id: string;
  admin_id: string;
  action: string;
  target_type: string;
  target_id: string | null;
  reason: string | null;
  metadata: Record<string, unknown> | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

export const searchAuditLog = (params: {
  adminId?: string;
  action?: string;
  targetType?: string;
  targetId?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
}) =>
  apiClient.get(`${BASE}/audit-log`, { params }).then(r => r.data);

export const getUserAuditLog = (userId: string, params: { page?: number; limit?: number } = {}) =>
  apiClient.get(`${BASE}/users/${userId}/audit-log`, { params }).then(r => r.data);

// ─── Admin Activity Analytics ────────────────────────────
export interface AdminActivitySummary {
  counts: { last24h: number; last7d: number; last30d: number };
  topAdmins: Array<{ adminId: string; username: string | null; email: string | null; count: number }>;
  topActions: Array<{ action: string; count: number }>;
  daily: Array<{ day: string; count: number }>;
}

export const getAdminActivitySummary = (): Promise<AdminActivitySummary> =>
  apiClient.get(`${BASE}/admin-activity/summary`).then(r => r.data);

// ─── Churn & Retention ───────────────────────────────────
export const getChurnRateTrend = (months = 12) =>
  apiClient.get(`${BASE}/churn/rate-trend`, { params: { months } }).then(r => r.data);

export const getCancellationReasons = () =>
  apiClient.get(`${BASE}/churn/cancellation-reasons`).then(r => r.data);

export const getRetentionCohort = (months = 6) =>
  apiClient.get(`${BASE}/churn/retention-cohort`, { params: { months } }).then(r => r.data);

export const getTrialFunnel = () =>
  apiClient.get(`${BASE}/churn/trial-funnel`).then(r => r.data);

export const getReferralFunnel = () =>
  apiClient.get(`${BASE}/churn/referral-funnel`).then(r => r.data);
